import React from 'react'

import {
  Box,
  InfiniteScroll,
  Spinner,
  Text,
  useClassNames,
} from '@factorialco/deprecated-design-system'
import { type ComplaintMessageFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { useComplaintMessages } from 'modules/complaints/hooks/useComplaintMessages'

import { ActivityElement, type ActivityItem } from './ActivityElement'
import './index.styles.css'

const stylesRefs = {
  feed: 'Feed_feed',
  bar: 'Feed_bar',
}

interface FeedProps {
  complaintId: number
}

const Feed: React.FC<FeedProps> = ({ complaintId }) => {
  const cx = useClassNames(stylesRefs)

  const {
    messages,
    controls: { fetchMore, hasMore },
    loading: isLoading,
    error,
  } = useComplaintMessages({
    complaintId,
    first: 10,
    sort: { field: 'created_at', order: 'DESC' },
  })

  const handleLoadMore = React.useCallback(() => {
    if (hasMore) {
      fetchMore()
    }
  }, [fetchMore, hasMore])

  const messageActivities: ActivityItem[] = messages.map((message: ComplaintMessageFragment) => ({
    id: `message-${message.id}`,
    title: message.isCompanyMessage
      ? i18n.t('complaints.activity.feed.message_company')
      : i18n.t('complaints.activity.feed.message_anonymous'),
    date: message.createdAt,
    text: message.text,
    files: message.messagefilesConnection?.edges?.map((edge) => ({
      id: edge.node.id,
      filename: edge.node.filename,
      path: edge.node.path,
    })),
    isMessage: true,
    type: message.isCompanyMessage ? 'message_company' : 'message_anonymous',
  }))

  const activities = messageActivities.sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime()
  })

  if (isLoading) {
    return (
      <Box paddingY="s32" paddingX="s48" justifyContent="center" alignItems="center" width="full">
        <Spinner />
      </Box>
    )
  }

  if (error) {
    return (
      <Box paddingY="s32" paddingX="s48" justifyContent="center" alignItems="center" width="full">
        <Text.Normal color="grey800">{i18n.t('common.error')}</Text.Normal>
      </Box>
    )
  }

  return (
    <Box className={cx('feed')} height="full">
      <InfiniteScroll
        loadMore={handleLoadMore}
        hasMore={hasMore}
        loader={
          <Box paddingTop="s24">
            <Box
              paddingY="s16"
              paddingX="s48"
              justifyContent="center"
              alignItems="center"
              width="full"
            >
              <Spinner />
            </Box>
          </Box>
        }
        initialLoad={false}
        threshold={10}
        pageStart={0}
      >
        <Box gap="s8" paddingRight="s32" position="relative">
          <Box className={cx('bar')} />
          {activities.map((activity, index) => (
            <ActivityElement
              key={activity.id + '_message_activity'}
              activity={activity}
              isLast={index === activities.length - 1}
              isFirst={index === activities.length - 1}
            />
          ))}
        </Box>
      </InfiniteScroll>
    </Box>
  )
}

export default Feed
